/* api/client — base HTTP client with in-memory access token + auto-refresh.
   Access token lives in module scope (lost on hard refresh — intentional).
   Refresh token lives in localStorage under REFRESH_KEY. */

const BASE = import.meta.env?.VITE_API_BASE ?? '/api/v1';
const REFRESH_KEY = 'nx-refresh-token';
const REQUEST_TIMEOUT_MS = 30_000;

/* SEC-F05: non-remember-me sessions store the refresh token in sessionStorage
   (cleared on tab close, not accessible cross-tab) rather than localStorage. */
let _useSessionStorage = false;
export const setSessionOnly = (v) => { _useSessionStorage = v; };

let _accessToken = null;
let _refreshPromise = null;

export const setToken = (token) => { _accessToken = token; };
export const getToken = () => _accessToken;
export const clearToken = () => { _accessToken = null; };

export const getRefreshToken = () => {
  try { return sessionStorage.getItem(REFRESH_KEY) || localStorage.getItem(REFRESH_KEY); } catch { return null; }
};
export const storeRefreshToken = (token) => {
  try {
    if (_useSessionStorage) {
      sessionStorage.setItem(REFRESH_KEY, token);
      localStorage.removeItem(REFRESH_KEY);
    } else {
      localStorage.setItem(REFRESH_KEY, token);
      sessionStorage.removeItem(REFRESH_KEY);
    }
  } catch { /* storage unavailable */ }
};
export const clearRefreshToken = () => {
  try { localStorage.removeItem(REFRESH_KEY); sessionStorage.removeItem(REFRESH_KEY); } catch { /* storage unavailable */ }
};

const doRefresh = async () => {
  const rt = getRefreshToken();
  if (!rt) throw new Error('No refresh token');
  const res = await fetch(`${BASE}/auth/refresh`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ refreshToken: rt }),
  });
  if (!res.ok) {
    clearRefreshToken();
    throw new Error('Refresh failed');
  }
  const data = await res.json();
  setToken(data.accessToken);
  storeRefreshToken(data.refreshToken);
  return data;
};

/* Single in-flight refresh: multiple 401s don't spawn multiple refresh calls */
export const refreshAccessToken = () => {
  if (!_refreshPromise) {
    _refreshPromise = doRefresh().finally(() => { _refreshPromise = null; });
  }
  return _refreshPromise;
};

const buildHeaders = (opts) => {
  /* Don't set Content-Type for FormData — the browser sets it with the correct multipart boundary. */
  const base = opts.body instanceof FormData ? {} : { 'Content-Type': 'application/json' };
  const headers = { ...base, ...opts.headers };
  /* Respect an explicit Authorization already in opts.headers (e.g. captured-after-refresh token
     passed by the 401 retry path).  Only fall back to the module-level _accessToken if absent. */
  if (!headers['Authorization'] && _accessToken) headers['Authorization'] = `Bearer ${_accessToken}`;
  return headers;
};

export const apiRequest = async (path, opts = {}) => {
  const { params, ...fetchOpts } = opts;
  let url = `${BASE}${path}`;
  if (params) {
    const qs = new URLSearchParams(params).toString();
    if (qs) url += '?' + qs;
  }
  const headers = buildHeaders(fetchOpts);

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);

  let res;
  try {
    res = await fetch(url, { ...fetchOpts, headers, signal: controller.signal });
  } catch (networkErr) {
    if (networkErr.name === 'AbortError') throw new Error('Request timed out. Please try again.');
    throw new Error('Connection failed. Please check your internet connection.');
  } finally {
    clearTimeout(timeoutId);
  }

  if (res.status === 401 && !opts._retry) {
    /* Capture the fresh token the instant the refresh resolves.  Passing it
       explicitly into the retry headers avoids any race where another async
       path could update _accessToken before buildHeaders runs.
       If the refresh fails (throws) or returns nothing, treat as session expired. */
    const freshToken = await refreshAccessToken().then(getToken).catch(() => null);
    if (!freshToken) {
      clearToken();
      clearRefreshToken();
      globalThis.dispatchEvent(new CustomEvent('nx-session-expired'));
      throw new Error('Session expired. Please sign in again.');
    }
    return apiRequest(path, {
      ...opts,
      _retry: true,
      headers: { ...opts.headers, Authorization: `Bearer ${freshToken}` },
    });
  }

  if (res.status === 403) {
    globalThis.dispatchEvent(new CustomEvent('nx-permission-denied'));
    throw Object.assign(new Error("You don't have permission to perform this action."), { status: 403 });
  }

  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    const rawMsg = Array.isArray(body.message) ? body.message.join('; ') : body.message;
    const msg = rawMsg ?? body.error?.message;
    switch (res.status) {
      case 400:
        throw Object.assign(new Error(msg || 'Bad request. Check your input and try again.'), { status: 400, body });
      case 404:
        throw Object.assign(new Error(msg || 'Resource not found.'), { status: 404, body });
      case 409:
        throw Object.assign(new Error(msg || 'Conflict: this resource already exists.'), { status: 409, body });
      case 422:
        throw Object.assign(new Error(msg || 'Validation failed. Check your input and try again.'), { status: 422, body });
      case 429:
        globalThis.dispatchEvent(new CustomEvent('nx-rate-limited', { detail: body }));
        throw Object.assign(new Error(msg || 'Too many requests. Please wait before trying again.'), { status: 429, body });
      default:
        throw Object.assign(new Error(msg || `Request failed (HTTP ${res.status}).`), { status: res.status, body });
    }
  }

  if (res.status === 204) return null;
  const text = await res.text();
  if (!text || !text.trim()) return null;
  return JSON.parse(text);
};

export const get    = (path, opts)        => apiRequest(path, { method: 'GET', ...opts });
export const post   = (path, body, opts)  => apiRequest(path, { method: 'POST',   body: body == null ? undefined : JSON.stringify(body), ...opts });
export const put    = (path, body, opts)  => apiRequest(path, { method: 'PUT',    body: body == null ? undefined : JSON.stringify(body), ...opts });
export const patch  = (path, body, opts)  => apiRequest(path, { method: 'PATCH',  body: body == null ? undefined : JSON.stringify(body), ...opts });
export const del    = (path, opts)        => apiRequest(path, { method: 'DELETE', ...opts });
/* Multipart file upload — omit Content-Type so the browser sets it with the correct boundary. */
export const upload = (path, formData, opts) => apiRequest(path, { method: 'POST', body: formData, headers: {}, ...opts });

export const apiClient = {
  get, post, put, patch, del,
  setToken, getToken, clearToken,
  getRefreshToken, storeRefreshToken, clearRefreshToken,
  refreshAccessToken,
  setSessionOnly,
};

export default apiClient;
